import { useCallback, useEffect, useState } from 'react'
import {
  CargoItemInterface,
  CargoPayload,
  PointItemInterface,
  Regularity,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../common/types/interfaces'

import {
  cargoValidations,
  minUploadFilesCount,
  CARGO_MY_ROUTE,
  USER_ROUTE,
} from '../../../../common/utils/constants'

import { useValidation } from '../../../../common/hooks/useValidation'
import { useInput } from '../../../../common/hooks/useInput'
import { FormStage } from '../../../../common/types/enums'
import { getApi } from '../../../../api/api'
import { useNavigate, useParams } from 'react-router-dom'
import { useFiles } from '../../../../common/hooks/useFiles'
import { useSelector } from 'react-redux'
import { selectCargoItem } from '../../../../store/slices/cargoListSlice'
import { RootState } from '../../../../store/store'
import { v4 } from 'uuid'
import { Buffer } from 'buffer'
import { useCommonLoader } from '../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = cargoValidations
const initialValue: CargoItemInterface = {
  name: '',
  description: '',
  startTime: new Date(Date.now()),
  endTime: new Date(new Date(Date.now()).setTime(new Date().getTime() + 1000 * 60 * 60)),
  regularity: Regularity.NO,
  cost: undefined,
  points: [],
  intercity: false,
  views: [],
  contacts: [],
  selected: [],
  cargo_units: [],
  transport_types: [],
  equipment_types: [],
  cargo_types: [],
  transport_side_types: [],
  transport_load_capacities: [],
  start_important: false,
  end_important: false,
}

export const useCargoForm = () => {
  const [data, setData] = useState<CargoItemInterface>(initialValue)
  const [formStage, setFormStage] = useState<FormStage>(FormStage.ONE)
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled1, setDisabled1] = useState<boolean>(true)
  const [disabled2, setDisabled2] = useState<boolean>(true)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [api] = useState(getApi())
  const navigate = useNavigate()
  const [filesFormData] = useState(new FormData())
  const [addresses, setAddresses] = useState<boolean>(false)
  const { inputHandler } = useInput(setData)
  const [isRoute] = useState<boolean>(true)
  const [editPointsData, setEditPointsData] = useState<PointItemInterface[]>()
  const [edit, setEdit] = useState<boolean>(false)
  const [cargoTypeValid, setCargoTypeValid] = useState<boolean>(true)
  const [transportTypeValid, setTransportTypeValid] = useState<boolean>(true)
  const afterActionRoute = localStorage.getItem('afterActionRoute')
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  //edit
  const { id } = useParams()

  const item = useSelector((state: RootState) => selectCargoItem(state, Number(id)))

  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()

  useEffect(() => {
    if (item) {
      setEdit(true)
      if (
        item.intercity &&
        item.points &&
        item.points.find((el) => el.address && el.address.value !== '')
      ) {
        setAddresses(true)
      }

      const cleanItem: CargoItemInterface = JSON.parse(JSON.stringify(item))
      delete cleanItem.createdAt
      delete cleanItem.updatedAt
      delete cleanItem.userId

      cleanItem &&
        cleanItem.points &&
        cleanItem.points.length > 0 &&
        setEditPointsData([
          ...cleanItem.points.map((attrs) => {
            delete attrs.cityId
            delete attrs.addressId
            delete attrs.createdAt
            delete attrs.updatedAt
            if (attrs.address) attrs.address.isDirty = false
            if (attrs.city) attrs.city.isDirty = false
            if (attrs.address) attrs.address.notValid = false
            if (attrs.city) attrs.city.notValid = false
            attrs.id = `a${v4()}`
            return attrs
          }),
        ])

      if (cleanItem.files && cleanItem.files?.length > 0) {
        fetchFiles(cleanItem.files)
        cleanItem.files = []
      }
      cleanItem.points = []
      cleanItem && setData(cleanItem)
    }
  }, [item, fetchFiles])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)
      const {
        points,
        priority,
        archived_user,
        archived_participant,
        deleted_user,
        deleted_participant,
        executorId,
        views,
        contacts,
        selected,
        cargo_units,
        can_see_user,
        can_take_user,
        can_see_partner,
        can_take_partner,
        ...rest
      } = data
      const payload: CargoPayload = {
        ...rest,
        cargo_units: cargo_units.map((el) => {
          return {
            width: Number(el.width),
            height: Number(el.height),
            length: Number(el.length),
            weight: Number(el.length),
            count: Number(el.count),
          }
        }),
        file: dataInit(files),
        points: points
          ? [
              ...points.map(({ id, ...keepAttrs }) => {
                if (keepAttrs.address) delete keepAttrs.address.isDirty
                if (keepAttrs.address) delete keepAttrs.address.notValid
                delete keepAttrs.city.isDirty
                delete keepAttrs.city.notValid
                return keepAttrs
              }),
            ]
          : [],
      }

      if (payload.id) {
        await api.updateCargo(payload)
      } else {
        await api.createCargo(payload)
      }

      await hideCommonLoader()

      if (afterActionRoute) {
        localStorage.removeItem('afterActionRoute')
        navigate(CARGO_MY_ROUTE, { replace: true })
        navigate(afterActionRoute)
        return
      }
      navigate(USER_ROUTE, { replace: true })
      navigate(CARGO_MY_ROUTE)
    } catch (error: any) {
      await hideCommonLoader()
    }
  }, [data, api, files, afterActionRoute, navigate, dataInit, showCommonLoader, hideCommonLoader])

  const toggleIntercity = () => {
    setData((prev) => ({ ...prev, intercity: !prev.intercity }))
    !data.intercity && setAddresses(false)
  }

  const toggleStartImportant = () => {
    setData((prev) => ({ ...prev, start_important: !prev.start_important }))
  }
  const toggleEndImportant = () => {
    setData((prev) => ({ ...prev, end_important: !prev.end_important }))
  }

  useEffect(() => {
    if (!data.start_important) setData((prev) => ({ ...prev, end_important: false }))
  }, [data.start_important])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled1(
      validationResult.filter((el) => el.name === 'name' && el.notValid).length > 0 ||
        validationResult.filter((el) => el.name === 'description' && el.notValid).length > 0 ||
        !transportTypeValid ||
        !cargoTypeValid,
    )
  }, [validationResult, transportTypeValid, cargoTypeValid])

  useEffect(() => {
    if (data.points)
      setDisabled2(
        data.points.length === 0 ||
          Boolean(
            !data.intercity && data.points.find((el) => el.address && !el.address.latitude),
          ) ||
          (data.intercity &&
            addresses &&
            Boolean(data.points.find((el) => el.address && !el.address.latitude))) ||
          Boolean(data.intercity && !addresses && data.points.find((el) => !el.city.latitude)),
      )
  }, [data.points, addresses, data.intercity])

  useEffect(() => {
    setDisabled(Boolean(files.length < minUploadFilesCount))
  }, [files])

  useEffect(() => {
    if (data.route) {
      console.log(
        `Route size ${(Buffer.byteLength(data.route, 'utf8') / 1024 / 1024).toFixed(2)}mb`,
      )
    }
  }, [data])

  return {
    inputHandler,
    data,
    formAction,
    getValidationResult,
    disabled,
    setDirty,
    setData,
    formStage,
    setFormStage,
    disabled1,
    disabled2,
    filesFormData,
    files,
    setFiles,
    pairs,
    setPairs,
    addresses,
    setAddresses,
    isRoute,
    edit,
    editPointsData,
    toggleIntercity,
    setCargoTypeValid,
    setTransportTypeValid,
    toggleStartImportant,
    toggleEndImportant,
  }
}
